// Pure helpers for the realty-zero-price-transaction decide flow (Baker,
// 2026-07-31). The card asks "What is the correct price for …?", so the
// rail renders a DOLLARS input and sends params.sale_price on verify; the
// backend writes it onto the linked property record (both the REAL column
// and the *_cents column this very detector reads) and resolves the issue.
//
// Before this, "verify" resolved the issue with nothing written — the
// price stayed $0 and the queue went quiet, which is the accuracy failure
// mode CLAUDE.md's "a number the team sees is a promise" section exists to
// stop. Mirrors the pm-revenue-gap flow (loan-fee-issue) exactly.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ — same
// convention as pm-entity-issue.ts / expiration-issue.ts.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface RealtyPriceIssueLike {
  detectorSlug: string;
}

/** Issues whose "verify" carries a sale price to the backend (and whose
 *  rail block renders the dollars input). */
export function isRealtyPriceIssue(issue: RealtyPriceIssueLike): boolean {
  return issue.detectorSlug === "realty-zero-price-transaction";
}

/** Typing the price IS the action — the backend 400s without it. */
export function salePriceRequired(issue: RealtyPriceIssueLike): boolean {
  return isRealtyPriceIssue(issue);
}

// Ceiling mirrors the backend's ($100M). Kept in sync deliberately: the
// backend stays the source of truth, this only enables/disables Apply.
const MAX_SALE_PRICE = 100_000_000;

/** Parse the dollars input, tolerating "$" and thousands separators the
 *  way an advisor pasting from BrokerMint would type them. Returns null
 *  when the value isn't a usable price. */
export function parseSalePriceInput(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0 || n > MAX_SALE_PRICE) return null;
  return Math.round(n * 100) / 100;
}

/** Client-side floor matching the backend's (positive, ≤ $100M). */
export function isValidSalePriceInput(raw: string): boolean {
  return parseSalePriceInput(raw) !== null;
}

function formatDollars(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  });
}

/** Plain-language before→after for the price-setting verify (UX rule #3)
 *  — replaces the generic verify copy when this slug is active. */
export function realtyPriceConsequence(issue: RealtyPriceIssueLike, raw: string): string | null {
  if (!isRealtyPriceIssue(issue)) return null;
  const price = parseSalePriceInput(raw);
  if (price !== null) {
    return `Records ${formatDollars(price)} as this deal's sale price on the property record, so it counts toward Realty's sales volume and stops being reported as a $0 close. Reversible for 60 seconds.`;
  }
  return "Enter the sale price from BrokerMint — recording it is the whole action here. If this wasn't a sale (a referral fee, say), use “Not an issue” below and note what it actually was.";
}
